import { useResource } from "hooks/useResource";
import { getInstitutions } from "services/content";

/**
 * Institution scope picker, shared by the notices / gallery / admissions
 * editors. Options come from the institutions content blob so a tag always
 * matches a real institution slug — typing them by hand silently produced
 * items that appeared nowhere.
 *
 * The value is a list of institution slugs. An EMPTY list means "not scoped":
 * the item shows on every institution's page. Selecting one or more limits it
 * to exactly those.
 */
export default function InstitutionTags({ value = [], onChange, label = "Show on" }) {
  const { data } = useResource(getInstitutions);
  const options = (data?.items || [])
    .map((i) => ({ tag: i.slug, name: i.name, short: i.short || i.slug, color: i.color }))
    .filter((o) => o.tag);
  const available = options.filter((o) => !value.includes(o.tag));

  const nameFor = (t) => options.find((o) => o.tag === t)?.name || t;
  const shortFor = (t) => options.find((o) => o.tag === t)?.short || t;
  const colorFor = (t) => options.find((o) => o.tag === t)?.color || "#005C40";

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[12px] font-semibold text-muted">{label}</span>

      {value.length === 0 && (
        <span className="text-[11.5px] text-muted italic">All institutions</span>
      )}

      {value.map((t) => (
        <span
          key={t}
          title={nameFor(t)}
          className="inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1.5 py-1 text-[11.5px] font-semibold"
          style={{ background: `${colorFor(t)}18`, color: colorFor(t) }}
        >
          {shortFor(t)}
          <button
            type="button"
            onClick={() => onChange(value.filter((x) => x !== t))}
            aria-label={`Remove ${shortFor(t)}`}
            className="bg-transparent border-none cursor-pointer text-[13px] leading-none p-0 opacity-70 hover:opacity-100"
            style={{ color: colorFor(t) }}
          >
            ×
          </button>
        </span>
      ))}

      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => e.target.value && onChange([...value, e.target.value])}
          className="px-2.5 py-1.5 border-[1.5px] border-line rounded-lg font-sans text-[12.5px] outline-none focus:border-green bg-white cursor-pointer"
        >
          <option value="">+ Institution</option>
          {available.map((o) => (
            <option key={o.tag} value={o.tag}>
              {o.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
